<template>
  <Component
    :is="componentType"
    :class="[
      'UIButton',
      `UIButton--theme-${theme}`,
      `UIButton--size-${size}`,
      `UIButton--style-${buttonStyle}`,
      {
        'UIButton--disabled': isDisabled,
        'UIButton--is-full': isFull,
        'UIButton--is-loading': isLoading,
      },
    ]"
    :disabled="isDisabled || isLoading"
  >
    <div v-if="isLoading" class="UIButton__loader">
      <UILoader class="UIButton__loader-icon" />
    </div>
    <div
      :class="[
        'UIButton__content',
        { 'UIButton__content--is-loading': isLoading },
      ]"
    >
      <div v-if="$slots['icon-before']" class="UIButton__icon-before">
        <slot name="icon-before" />
      </div>
      <div>
        <slot />
      </div>
      <div v-if="$slots['icon-after']" class="UIButton__icon-after">
        <slot name="icon-after" />
      </div>
    </div>
  </Component>
</template>

<script lang="ts" setup>
import { computed, useAttrs } from 'vue'

import UILoader from '../loaders/UILoader.vue'

export type IButtonThemes =
  'primary' | 'neutral' | 'white' | 'success' | 'error' | 'warning'

withDefaults(
  defineProps<{
    /**
     * Determines the size of the button (preset list)
     * @default 'md'
     */
    size?: 'sm' | 'md' | 'lg'
    /**
     * Determines the theme of the button (preset list)
     * @default 'primary'
     */
    theme?: IButtonThemes
    /**
     * Disables the button
     * @default false
     */
    isDisabled?: boolean
    /**
     * Sets button width to 100%
     * @default false
     */
    isFull?: boolean
    /**
     * Replaces button content with a spinner
     * @default false
     */
    isLoading?: boolean
    /**
     * Sets background to white and font/border colors to the theme
     * @default 'filled'
     */
    buttonStyle?: 'filled' | 'text'
  }>(),
  {
    size: 'md',
    theme: 'primary',
    isDisabled: false,
    isFull: false,
    isLoading: false,
    buttonStyle: 'filled',
  },
)

const componentType = computed(() => {
  const attrs = useAttrs()
  if (attrs.to) {
    return 'router-link'
  }
  if (attrs.href) {
    return 'a'
  }

  return 'button'
})
</script>

<style lang="scss" scoped>
.UIButton {
  @include trans(0.2s);
  --border-width: 1px;

  align-items: center;
  text-decoration: none;
  width: fit-content;
  border-radius: 4px;
  font-weight: 400;
  text-align: center;
  white-space: nowrap;
  position: relative;
  cursor: pointer;
  display: inline-flex;

  background: var(--background);
  color: var(--color);
  // border: var(--border-width) solid var(--border-color);
  border: none;
  padding: var(--padding);
  font-size: var(--font-size);
  line-height: var(--line-height);
  --icon-size: var(--size);

  .UIButton__loader {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .UIButton__content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .UIButton__icon-before {
    display: flex;
    align-items: center;
    margin-right: 0.5rem;
  }
  .UIButton__icon-after {
    display: flex;
    align-items: center;
    margin-left: 0.5rem;
  }

  &.UIButton--is-loading {
    pointer-events: none;
  }
  .UIButton__content--is-loading {
    opacity: 0;
  }

  // Sizes
  &.UIButton--size-sm {
    --padding: 4px 8px;
    --font-size: 12px;
    --line-height: 16px;
    :deep(.UIIcon-wrapper) {
      --size: var(--icon-size, 0.75rem);
    }
  }
  &.UIButton--size-md {
    --padding: 8px 16px;
    --font-size: 14px;
    --line-height: 20px;
    :deep(.UIIcon-wrapper) {
      --size: var(--icon-size, 1rem);
    }
  }
  &.UIButton--size-lg {
    --padding: 8px 16px;
    --font-size: 18px;
    --line-height: 28px;
    :deep(.UIIcon-wrapper) {
      --size: var(--icon-size, 1.5rem);
    }
  }

  // Button themes
  @mixin buttonTheme($main, $hover, $active, $text, $loader: $text) {
    --background: #{$main};
    --border-color: #{$main};
    --color: #{$text};
    --loader-color: #{$loader};

    &:hover {
      --background: #{$hover};
    }
    &:active {
      --background: #{$active};
    }
  }
  @mixin buttonTextTheme($hover, $active, $text, $textHover, $textActive) {
    &.UIButton--style-text {
      --background: none;
      --border-color: none;
      --color: #{$text};
      --loader-color: #{$text};

      &:hover {
        --background: #{$hover};
        --color: #{$textHover};
      }
      &:active {
        --background: #{$active};
        --color: #{$textActive};
      }
    }
  }

  &.UIButton--theme-primary {
    @include buttonTheme(
      $color-primary-40,
      $color-primary-50,
      $color-primary-60,
      $color-white
    );
    @include buttonTextTheme(
      $color-primary-5,
      $color-primary-10,
      $color-primary-50,
      $color-primary-50,
      $color-primary-50
    );
  }

  &.UIButton--theme-neutral {
    @include buttonTheme(
      $color-neutral-10,
      $color-neutral-20,
      $color-neutral-30,
      $color-neutral-140
    );

    @include buttonTextTheme(
      $color-neutral-10,
      $color-neutral-20,
      $color-neutral-140,
      $color-neutral-140,
      $color-neutral-140
    );
  }
  // TODO: this is missing from the DS
  &.UIButton--theme-white {
    @include buttonTheme(
      $color-white,
      $color-neutral-20,
      $color-neutral-30,
      $color-primary-50
    );

    @include buttonTextTheme(
      $color-neutral-10,
      $color-neutral-20,
      $color-neutral-140,
      $color-neutral-140,
      $color-neutral-140
    );
  }

  &.UIButton--theme-success {
    @include buttonTheme(
      $color-success-40,
      $color-success-50,
      $color-success-60,
      $color-white
    );
    @include buttonTextTheme(
      $color-success-5,
      $color-success-10,
      $color-success-60,
      $color-success-70,
      $color-success-80
    );
  }

  &.UIButton--theme-error {
    @include buttonTheme(
      $color-error-40,
      $color-error-50,
      $color-error-60,
      $color-white
    );
    @include buttonTextTheme(
      $color-error-5,
      $color-error-10,
      $color-error-60,
      $color-error-70,
      $color-error-80
    );
  }
  &.UIButton--theme-warning {
    @include buttonTheme(
      $color-warning-40,
      $color-warning-50,
      $color-warning-60,
      $color-white
    );
    @include buttonTextTheme(
      $color-warning-5,
      $color-warning-10,
      $color-warning-60,
      $color-warning-70,
      $color-warning-80
    );
  }

  &.UIButton--disabled {
    pointer-events: none;
    --background: #{$color-neutral-10};
    --border-color: #{$color-neutral-10};
    --color: #{$color-neutral-50};
  }
  &.UIButton--is-full {
    width: 100%;
  }

  .UIButton__loader-icon {
    --color: var(--loader-color);
    --size: var(--size, 1rem);
    --size: 1rem;
  }
}
</style>
